import { Component, EventEmitter, inject, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ListingFilter } from '../../../core/models/home.model';
import { LookupService } from '../../../core/services/lookup.service';
import {LookupItem, Settlement} from '../../../core/models/lookup.model';

@Component({
  selector: 'app-filter-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="filter-panel">
      <div class="filter-main">
        <div class="filter-row">
          <div class="filter-field deal-type">
            <label><i class="fas fa-exchange-alt"></i> Əməliyyat</label>
            <div class="select-wrapper">
              <select class="control" [(ngModel)]="filters.dealType" (change)="onFilterChange()">
                <option value="">Alqı-satqı / İcarə</option>
                <option value="sale">Alış</option>
                <option value="rent">Kirayə</option>
              </select>
            </div>
          </div>

          <div class="filter-field property-type">
            <label><i class="fas fa-home"></i> Əmlakın növü</label>
            <div class="select-wrapper">
              <select class="control" [(ngModel)]="filters.propertyType" (change)="onFilterChange()">
                <option value="">Hamısı</option>
                <option *ngFor="let type of propertyTypes" [value]="type.id">{{type.name}}</option>
              </select>
            </div>
          </div>

          <div class="filter-field rooms">
            <label><i class="fas fa-bed"></i> Otaq sayı</label>
            <div class="room-buttons">
              @for (room of [1, 2, 3, 4, '5+']; track room) {
                <button
                  type="button"
                  [class.active]="isRoomSelected(room)"
                  (click)="toggleRoom(room)">
                  {{ room }}
                </button>
              }
            </div>
          </div>
        </div>

        <div class="filter-row second-row">
          <div class="filter-field price-range">
            <label><i class="fas fa-tag"></i> Qiymət (₼)</label>
            <div class="range-inputs">
              <input type="number" class="control" [(ngModel)]="filters.minPrice" placeholder="Min" (change)="onFilterChange()">
              <span class="separator">-</span>
              <input type="number" class="control" [(ngModel)]="filters.maxPrice" placeholder="Maks" (change)="onFilterChange()">
            </div>
          </div>

          <div class="filter-field city">
            <label><i class="fas fa-city"></i> Şəhər</label>
            <div class="select-wrapper">
              <select class="control" [(ngModel)]="filters.cityId" (change)="onCityChange()">
                <option [ngValue]="undefined">Şəhər seçin</option>
                <option *ngFor="let area of cities" [value]="area.id">{{area.name}}</option>
              </select>
            </div>
          </div>

          <div class="filter-field district" *ngIf="districts.length > 0">
            <label><i class="fas fa-map-marker-alt"></i> Rayon</label>
            <div class="select-wrapper">
              <select class="control" [(ngModel)]="filters.districtId" (change)="onDistrictChange()">
                <option [ngValue]="undefined">Rayon seçin</option>
                <option *ngFor="let d of districts" [value]="d.id">{{d.name}}</option>
              </select>
            </div>
          </div>

          <div class="filter-field settlement" *ngIf="settlements.length > 0">
            <label><i class="fas fa-map-signs"></i> Qəsəbə</label>
            <div class="select-wrapper">
              <select class="control" [(ngModel)]="filters.settlementId" (change)="onFilterChange()">
                <option [ngValue]="undefined">Qəsəbə seçin</option>
                <option *ngFor="let s of settlements" [value]="s.id">{{s.name}}</option>
              </select>
            </div>
          </div>

          <div class="filter-field metro" *ngIf="metros.length > 0">
            <label><i class="fas fa-subway"></i> Metro</label>
            <div class="select-wrapper">
              <select class="control" [(ngModel)]="filters.metroId" (change)="onFilterChange()">
                <option [ngValue]="undefined">Metro seçin</option>
                <option *ngFor="let m of metros" [value]="m.id">{{m.name}}</option>
              </select>
            </div>
          </div>

          <div class="filter-actions">
            <button class="btn reset-btn" (click)="reset()" title="Sıfırla">
              <i class="fas fa-sync-alt"></i>
            </button>
            <button class="btn apply-btn" (click)="applyArea()">
              <i class="fas fa-search"></i> Axtar
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      --fp-height: 52px;
      --fp-radius: 12px;
      --fp-border: #e2e8f0;
      --fp-focus: #3b82f6;
      --fp-bg: #ffffff;
      --fp-text: #1e293b;
      --fp-label: #64748b;
      --fp-placeholder: #94a3b8;
      --fp-gap: 20px;
    }

    .filter-panel {
      background: var(--fp-bg);
      padding: 24px;
      border-radius: 20px;
      box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
      margin-bottom: 32px;
      border: 1px solid #f1f5f9;
    }

    .filter-main {
      display: flex;
      flex-direction: column;
      gap: 24px;
    }

    .filter-row {
      display: grid;
      grid-template-columns: 1fr 1fr 1.5fr;
      gap: var(--fp-gap);
      align-items: end;
    }

    .filter-row.second-row {
      grid-template-columns: 1.2fr 1.8fr auto;
    }

    .filter-field {
      display: flex;
      flex-direction: column;
      gap: 8px;

      label {
        font-size: 13px;
        font-weight: 600;
        color: var(--fp-label);
        display: flex;
        align-items: center;
        gap: 6px;

        i {
          font-size: 14px;
          color: var(--fp-focus);
          opacity: 0.8;
        }
      }
    }

    .control {
      height: var(--fp-height);
      padding: 0 16px;
      border: 1.5px solid var(--fp-border);
      border-radius: var(--fp-radius);
      font-size: 15px;
      font-weight: 500;
      color: var(--fp-text);
      background: #f8fafc;
      outline: none;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      width: 100%;

      &::placeholder {
        color: var(--fp-placeholder);
      }

      &:focus {
        border-color: var(--fp-focus);
        background: #fff;
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
      }

      &:hover:not(:focus) {
        border-color: #cbd5e1;
        background: #f1f5f9;
      }
    }

    .select-wrapper {
      position: relative;

      &::after {
        content: "\\f078";
        font-family: 'Font Awesome 5 Free';
        font-weight: 900;
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
        font-size: 12px;
        color: var(--fp-label);
      }
    }

    select.control {
      appearance: none;
      padding-right: 40px;
    }

    .room-buttons {
      display: flex;
      gap: 6px;
      height: var(--fp-height);
      background: #f1f5f9;
      padding: 4px;
      border-radius: var(--fp-radius);

      button {
        flex: 1;
        border: none;
        background: transparent;
        border-radius: calc(var(--fp-radius) - 4px);
        cursor: pointer;
        font-size: 14px;
        font-weight: 700;
        color: var(--fp-label);
        transition: all 0.2s;

        &.active {
          background: #fff;
          color: var(--fp-focus);
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        &:hover:not(.active) {
          color: var(--fp-text);
          background: rgba(255,255,255,0.5);
        }
      }
    }

    .range-inputs {
      display: flex;
      gap: 0;
      align-items: center;
      background: #f8fafc;
      border: 1.5px solid var(--fp-border);
      border-radius: var(--fp-radius);
      overflow: hidden;
      transition: all 0.2s;

      &:focus-within {
        border-color: var(--fp-focus);
        background: #fff;
        box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
      }

      .control {
        border: none;
        background: transparent;
        border-radius: 0;
        height: calc(var(--fp-height) - 3px);

        &:focus {
          box-shadow: none;
        }
      }

      .separator {
        color: var(--fp-placeholder);
        font-weight: 300;
        padding: 0 4px;
      }
    }

    .search-wrapper {
      position: relative;

      .control {
        padding-right: 45px;
      }

      .search-icon {
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: var(--fp-placeholder);
        font-size: 16px;
      }
    }

    .filter-actions {
      display: flex;
      gap: 12px;
    }

    .btn {
      height: var(--fp-height);
      border-radius: var(--fp-radius);
      font-weight: 700;
      font-size: 15px;
      cursor: pointer;
      transition: all 0.2s;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .reset-btn {
      width: var(--fp-height);
      background: #f1f5f9;
      border: none;
      color: var(--fp-label);

      &:hover {
        background: #e2e8f0;
        color: #ef4444;
        transform: rotate(-30deg);
      }
    }

    .apply-btn {
      background: var(--fp-focus);
      border: none;
      color: #fff;
      padding: 0 32px;
      flex-grow: 1;

      &:hover {
        background: #2563eb;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
      }

      &:active {
        transform: translateY(0);
      }
    }

    @media (max-width: 1024px) {
      .filter-row, .filter-row.second-row {
        grid-template-columns: 1fr 1fr;
      }
      .filter-actions {
        grid-column: span 2;
      }
    }

    @media (max-width: 768px) {
      .filter-row, .filter-row.second-row {
        grid-template-columns: 1fr;
      }
      .filter-actions {
        grid-column: span 1;
      }
      .filter-panel {
        padding: 16px;
      }
    }
  `]
})
export class FilterPanelComponent implements OnInit {
  private lookupService = inject(LookupService);
  private platformId = inject(PLATFORM_ID);
  propertyTypes: LookupItem[] = [];
  cities: LookupItem[] = [];
  districts: LookupItem[] = [];
  settlements: Settlement[] = [];
  metros: LookupItem[] = [];

  filters: ListingFilter = {
    dealType: '',
    propertyType: '',
    rooms: [],
    minPrice: undefined,
    maxPrice: undefined,
    cityId: undefined,
    districtId: undefined,
    settlementId: undefined,
    metroId: undefined,
    location: ''
  };

  @Output() filterChanged = new EventEmitter<ListingFilter>();
  @Output() resetFilters = new EventEmitter<void>();
  @Output() areaModalApplied = new EventEmitter<ListingFilter>();

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.lookupService.getPropertyTypes().subscribe(res => {
        this.propertyTypes = res;
      });
      this.lookupService.getAdministrativeAreas().subscribe(res => {
        this.cities = res;
      });
    }
  }

  onCityChange() {
    this.filters.districtId = undefined;
    this.filters.settlementId = undefined;
    this.filters.metroId = undefined;
    this.districts = [];
    this.settlements = [];
    this.metros = [];

    const city = this.cities.find(c => c.id == this.filters.cityId);
    if (city) {
      if (city.name === 'Bakı') {
        this.lookupService.getBakuDistricts().subscribe(res => this.districts = res);
        this.lookupService.getBakuMetroStations().subscribe(res => this.metros = res);
      } else if (city.name === 'Abşeron') {
        this.lookupService.getAbsheronAreas().subscribe(res => this.districts = res);
      }
    }
    this.onFilterChange();
  }

  onDistrictChange() {
    this.filters.settlementId = undefined;
    this.settlements = [];

    const city = this.cities.find(c => c.id == this.filters.cityId);
    if (city?.name === 'Bakı' && this.filters.districtId) {
      this.lookupService.getSettlements(this.filters.districtId).subscribe(res => this.settlements = res);
    }
    this.onFilterChange();
  }

  toggleRoom(room: number | string) {
    if (!this.filters.rooms) this.filters.rooms = [];
    const roomVal = typeof room === 'string' ? 5 : room;
    const index = this.filters.rooms.indexOf(roomVal);
    if (index > -1) {
      this.filters.rooms.splice(index, 1);
    } else {
      this.filters.rooms.push(roomVal);
    }
    this.onFilterChange();
  }

  isRoomSelected(room: number | string): boolean {
    const roomVal = typeof room === 'string' ? 5 : room;
    return this.filters.rooms?.includes(roomVal) || false;
  }

  onFilterChange() {
    this.filterChanged.emit({ ...this.filters });
  }

  reset() {
    this.filters = {
      dealType: '',
      propertyType: '',
      rooms: [],
      minPrice: undefined,
      maxPrice: undefined,
      cityId: undefined,
      districtId: undefined,
      settlementId: undefined,
      metroId: undefined,
      location: ''
    };
    this.districts = [];
    this.settlements = [];
    this.metros = [];
    this.resetFilters.emit();
  }

  applyArea() {
    this.areaModalApplied.emit({ ...this.filters });
  }
}
